import { Check, Plus, Trash2 } from 'lucide-react';

const newId = () => `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const TaskBoard = ({ value, onChange, suggestions, weeks, progress }) => {
  const tasks = value || [];
  const set = (next) => onChange(next.length ? next : null);
  const update = (id, patch) => set(tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const titles = new Set(tasks.map((t) => t.title.trim().toLowerCase()));
  const fresh = suggestions.filter((s) => !titles.has(s.title.toLowerCase()));
  return (
    <div className="py-6" data-testid="task-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-matte/70" data-testid="task-progress">{progress.done} of {progress.total} tasks complete · {progress.pct}%</div>
        {fresh.length > 0 && (
          <button type="button" onClick={() => set([...tasks, ...fresh.map((s) => ({ ...s, id: newId(), due: null, done: false }))])}
            className="rounded-full border border-gold/50 px-4 py-2 text-[11px] uppercase tracking-[0.16em] hover:bg-gold/10 transition-colors duration-200" data-testid="task-add-suggestions-button">
            Add {fresh.length} suggested tasks from my blueprint
          </button>
        )}
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-matte/10 overflow-hidden"><div className="h-full bg-gold transition-[width] duration-500" style={{ width: `${progress.pct}%` }} /></div>
      {Object.entries(weeks).map(([w, label]) => {
        const list = tasks.filter((t) => String(t.week) === w);
        const wp = progress.weeks[w] || { done: 0, total: 0 };
        return (
          <div key={w} className="mt-8" data-testid={`task-week-${w}`}>
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="font-serif-display text-2xl text-matte">{label}</h3>
              <span className="text-[11px] uppercase tracking-[0.16em] text-matte/50">{wp.done}/{wp.total}</span>
            </div>
            <ul className="mt-3 space-y-2">
              {list.map((t) => (
                <li key={t.id} className="flex flex-wrap sm:flex-nowrap items-center gap-2 rounded-xl border border-matte/10 bg-ivory/60 p-2" data-testid={`task-${t.id}`}>
                  <button type="button" role="checkbox" aria-checked={t.done} aria-label="Mark task done" onClick={() => update(t.id, { done: !t.done })}
                    className={`h-6 w-6 shrink-0 rounded-md border flex items-center justify-center ${t.done ? 'bg-matte border-matte text-ivory' : 'border-matte/30 bg-white'}`} data-testid={`task-done-${t.id}`}>
                    {t.done && <Check size={14} />}
                  </button>
                  <textarea rows={2} value={t.title} maxLength={300} onChange={(e) => update(t.id, { title: e.target.value })} aria-label="Task"
                    style={{ fieldSizing: 'content' }}
                    className={`flex-1 min-w-[60%] sm:min-w-0 resize-none bg-transparent px-2 py-1.5 text-sm leading-snug focus:outline-none ${t.done ? 'line-through text-matte/50' : 'text-matte'}`} data-testid={`task-title-${t.id}`} />
                  <input type="date" value={t.due || ''} onChange={(e) => update(t.id, { due: e.target.value || null })} aria-label="Target date"
                    className="ml-8 sm:ml-0 rounded-lg border border-matte/15 bg-white px-2 py-1 text-xs" data-testid={`task-due-${t.id}`} />
                  <button type="button" onClick={() => set(tasks.filter((x) => x.id !== t.id))} aria-label="Remove task" className="p-1.5 text-matte/40 hover:text-red-700" data-testid={`task-remove-${t.id}`}><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => set([...tasks, { id: newId(), week: Number(w), title: 'New task', due: null, done: false }])}
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-matte/60 hover:text-matte" data-testid={`task-add-${w}`}>
              <Plus size={12} /> Add task
            </button>
          </div>
        );
      })}
    </div>
  );
};
